import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService } from '@uniqcamp/shared';
import type { ParentSchoolOption, StudentOption, StudentsResponse } from '../types';

const STORAGE_KEY = 'parent-selected-school-id';

function deriveSchools(students: StudentOption[]): ParentSchoolOption[] {
  const bySchool = new Map<string, ParentSchoolOption>();
  students.forEach((s) => {
    const school = s.schoolId;
    if (!school?._id) return;
    if (!bySchool.has(school._id)) {
      bySchool.set(school._id, {
        _id: school._id,
        name: school.name,
        uniqueCode: school.uniqueCode,
        address: school.address,
        students: [],
      });
    }
    const row = bySchool.get(school._id)!;
    row.students.push({ _id: s._id, name: s.name, class: s.class, section: s.section });
  });
  return Array.from(bySchool.values());
}

interface SelectedSchoolContextValue {
  selectedSchoolId: string | null;
  setSelectedSchoolId: (id: string | null) => void;
  schools: ParentSchoolOption[];
  schoolsLoading: boolean;
  hasMultipleSchools: boolean;
  refetchSchools: () => void;
}

const SelectedSchoolContext = createContext<SelectedSchoolContextValue | null>(null);

export function SelectedSchoolProvider({ children }: { children: React.ReactNode }) {
  const [selectedSchoolId, setSelectedSchoolIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(STORAGE_KEY);
  });

  const setSelectedSchoolId = useCallback((id: string | null) => {
    setSelectedSchoolIdState(id);
    if (typeof window !== 'undefined') {
      if (id) window.localStorage.setItem(STORAGE_KEY, id);
      else window.localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const { data, isLoading, refetch } = useQuery<StudentsResponse>(
    'parent-students',
    () => apiClient.get('/students').then((r: { data: unknown }) => r.data as StudentsResponse),
    { staleTime: 60000 }
  );

  const students = useMemo<StudentOption[]>(() => data?.students ?? [], [data]);
  const schools = useMemo(() => deriveSchools(students), [students]);
  const hasMultipleSchools = schools.length > 1;

  useEffect(() => {
    if (selectedSchoolId && schools.length > 0 && !schools.some((s) => s._id === selectedSchoolId)) {
      setSelectedSchoolIdState(null);
      if (typeof window !== 'undefined') window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [selectedSchoolId, schools]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!authService.isAuthenticated()) return;
    const u = authService.getStoredUser();
    if (!u || u.role !== 'PARENT') return;
    authService.refreshSessionUserProfile({ schoolId: selectedSchoolId }).catch(() => {});
  }, [selectedSchoolId]);

  const value: SelectedSchoolContextValue = useMemo(
    () => ({
      selectedSchoolId,
      setSelectedSchoolId,
      schools,
      schoolsLoading: isLoading,
      hasMultipleSchools,
      refetchSchools: refetch,
    }),
    [selectedSchoolId, setSelectedSchoolId, schools, isLoading, hasMultipleSchools, refetch]
  );

  return (
    <SelectedSchoolContext.Provider value={value}>
      {children}
    </SelectedSchoolContext.Provider>
  );
}

export function useSelectedSchool() {
  const ctx = useContext(SelectedSchoolContext);
  if (!ctx) throw new Error('useSelectedSchool must be used within SelectedSchoolProvider');
  return ctx;
}
